import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { UrlPrefixValidatorUiOptions } from './schema';
import { useApi } from '@backstage/core-plugin-api';
import { n8nManagerApiRef } from '@mercedes-benz/backstage-plugin-aiecosystem';

type ValidationState = 'idle' | 'validating' | 'valid' | 'invalid' | 'error';

const SPACE_PATTERN = /\s/;
const ALLOWED_CHARS_PATTERN = /^[a-z0-9-]*$/;

export const UrlPrefixValidator = (
  props: FieldExtensionComponentProps<string, UrlPrefixValidatorUiOptions>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    schema: {
      title = 'URL prefix',
      description = 'Your n8n instance will be accessible via: `https://your-url-prefix.n8n.app.corpintra.net`.',
      minLength = 3,
      maxLength = 12,
    },
    uiSchema,
    formData,
  } = props;

  const uiOptions =
    (uiSchema?.['ui:options'] as UrlPrefixValidatorUiOptions) ?? {};
  const debounceMs = uiOptions.debounceMs ?? 400;

  const fullPattern = useMemo(
    () => new RegExp(`^[a-z][a-z0-9-]{${minLength - 1},${maxLength - 1}}$`),
    [minLength, maxLength],
  );

  const n8nManagerApi = useApi(n8nManagerApiRef);
  const [urlPrefix, setUrlPrefix] = useState<string>(formData ?? '');
  const [validationState, setValidationState] =
    useState<ValidationState>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Performs validation of the URL prefix: format checks first (spaces,
   * disallowed characters, length), then a debounced uniqueness check
   * against the n8n Instance Manager API.
   */
  const performValidation = useCallback(
    (value: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!value || value.trim().length === 0) {
        setValidationState('error');
        setValidationError('Error. This field is required.');
        onChange(undefined as any);
        return;
      }

      if (SPACE_PATTERN.test(value)) {
        setValidationState('error');
        setValidationError('Spaces are not allowed. Use dashes ( - ) instead.');
        onChange(undefined as any);
        return;
      }

      if (!ALLOWED_CHARS_PATTERN.test(value)) {
        setValidationState('error');
        setValidationError(
          'Only lowercase letters (a-z), numbers (0-9), and dashes (-) are allowed.',
        );
        onChange(undefined as any);
        return;
      }

      if (!fullPattern.test(value)) {
        setValidationState('invalid');
        setValidationError(
          `The URL prefix must start with a letter and be ${minLength}-${maxLength} characters long. Current: ${value.length} characters.`,
        );
        onChange(undefined as any);
        return;
      }

      setValidationState('validating');
      setValidationError(null);

      debounceTimerRef.current = setTimeout(async () => {
        try {
          const result = await n8nManagerApi.validateUriPrefix(value);

          if (result.available) {
            setValidationState('valid');
            setValidationError(null);
            onChange(value);
          } else {
            setValidationState('invalid');
            setValidationError(
              `Error. The URL prefix "${value}" already exists. Provide a different URL prefix for your instance.`,
            );
            onChange(undefined as any);
          }
        } catch (err) {
          setValidationState('error');
          const errorMessage =
            err instanceof Error
              ? err.message
              : 'Unknown error occurred while checking URL prefix availability.';
          setValidationError(errorMessage);
          onChange(undefined as any);
        }
      }, debounceMs);
    },
    [n8nManagerApi, debounceMs, minLength, maxLength, fullPattern, onChange],
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Perform initial validation if formData is present (e.g., after "Start over")
  useEffect(() => {
    if (formData && formData.trim().length > 0) {
      performValidation(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setUrlPrefix(val);
    performValidation(val);
  };

  const hasRawErrors = rawErrors && rawErrors.length > 0;
  const isInvalid =
    hasRawErrors ||
    validationState === 'invalid' ||
    validationState === 'error';
  const isValid = validationState === 'valid';
  const isValidating = validationState === 'validating';

  const renderEndAdornment = () => {
    if (isValidating) {
      return (
        <InputAdornment position="end">
          <CircularProgress size={20} />
        </InputAdornment>
      );
    }

    if (isValid && urlPrefix.trim().length > 0) {
      return (
        <InputAdornment position="end">
          <CheckCircleIcon color="success" />
        </InputAdornment>
      );
    }

    if (isInvalid) {
      return (
        <InputAdornment position="end">
          <ErrorOutlineIcon color="error" />
        </InputAdornment>
      );
    }

    return undefined;
  };

  const getHelperText = () => {
    if (validationError) {
      return validationError;
    }

    if (hasRawErrors) {
      return rawErrors[0];
    }

    if (isValidating) {
      return 'Checking availability...';
    }

    if (isValid && urlPrefix.trim().length > 0) {
      return `Success! The URL prefix "${urlPrefix}" is available.`;
    }

    return description;
  };

  return (
    <Box>
      <TextField
        fullWidth
        label={title}
        placeholder={uiSchema?.['ui:placeholder'] ?? 'e.g., my-instance'}
        required={required}
        value={urlPrefix}
        onChange={handleInputChange}
        variant="standard"
        error={isInvalid}
        color={isValid ? 'success' : undefined}
        focused={isValid}
        helperText={getHelperText()}
        InputProps={{
          endAdornment: renderEndAdornment(),
        }}
        inputProps={{
          pattern: '^[a-z][a-z0-9-]*$',
          title: 'Only lowercase letters, numbers, and dashes allowed.',
          maxLength: maxLength,
        }}
      />
    </Box>
  );
};
